'use client';

import useConfig from '@/hooks/useConfig';
import { useEffect, useMemo, useState } from 'react';
import { useWidgetStore } from '@/providers/widget.provider';
import ReassuranceSection from './ReassuranceSection';
import { useParams, useSearchParams } from 'next/navigation';
import { getCountryService } from '@/actions/resources/getCountryService';
import { getShop } from '@/actions/resources/getShop';
import { searchAvailabilitiesByService } from '@/actions/oneff/searchAvailabilitiesByService';
import { adaptOneFFAvailabilitiesByServiceItemOptionTimeSlotToSlotInterface } from '@/adapters/time-slots.adapter';
import Skeleton from '@repo/ui/Skeleton';
import { BookingStatus } from '@repo/common/interfaces';

const ReasssuranceWrapper = () => {
  const { currentStep } = useConfig();
  const params = useParams<{ locale: string; countryCode: string }>();
  const searchParams = useSearchParams();
  const {
    countryService,
    shop,
    timeSlot,
    setCountryService,
    setShop,
    setTimeSlot,
  } = useWidgetStore((state) => state);
  const [initialized, setInitialized] = useState<boolean>();
  const [isLoadingCountryService, setIsLoadingCountryService] =
    useState<boolean>();
  const [isLoadingShop, setIsLoadingShop] = useState<boolean>();
  const [isLoadingTimeSlot, setIsLoadingTimeSlot] = useState<boolean>();

  const isInitializingData = useMemo(() => {
    return isLoadingCountryService || isLoadingShop || isLoadingTimeSlot;
  }, [isLoadingCountryService, isLoadingShop, isLoadingTimeSlot]);

  useEffect(() => {
    if (initialized) {
      return;
    }

    if (
      searchParams?.get('sku') &&
      searchParams?.get('sku') !== countryService?.sku
    ) {
      setIsLoadingCountryService(true);
      getCountryService(
        params?.countryCode,
        searchParams?.get('sku') as string,
        params?.locale,
      )
        ?.then((_countryService) => {
          setCountryService(_countryService);
        })
        ?.finally(() => {
          setIsLoadingCountryService(false);
        });
    }

    if (searchParams?.get('shop')) {
      setIsLoadingShop(true);
      getShop(searchParams?.get('shop') as string)
        ?.then((_shop) => {
          setShop(_shop);
        })
        ?.finally(() => {
          setIsLoadingShop(false);
        });
    }

    if (
      shop &&
      searchParams?.get('start_sec') &&
      searchParams?.get('fulfiller_id') &&
      searchParams?.get('option_id') &&
      (searchParams?.get('fulfiller_id') !== timeSlot?.fulfillerId ||
        searchParams?.get('option_id') !== timeSlot?.optionId ||
        searchParams?.get('start_sec') !== timeSlot?.startSec?.toString())
    ) {
      setIsLoadingTimeSlot(true);
      searchAvailabilitiesByService(
        params?.countryCode,
        searchParams?.get('sku') as string,
        shop,
        Number(searchParams?.get('start_sec') as string),
        1,
      )
        ?.then((_timeSlot) => {
          const fulfillerId = _timeSlot?.availabilities?.[0]?.fulfiller_id;
          const optionId =
            _timeSlot?.availabilities?.[0]?.options?.[0]?.option_id;
          const slot =
            _timeSlot?.availabilities?.[0]?.options?.[0]?.time_slots?.[0];
          setTimeSlot(
            adaptOneFFAvailabilitiesByServiceItemOptionTimeSlotToSlotInterface(
              slot,
              fulfillerId,
              optionId,
              shop?.timeZone,
            ),
          );
        })
        ?.finally(() => {
          setIsLoadingTimeSlot(false);
        });
    }

    setInitialized(true);
  }, [searchParams, initialized, setInitialized]);

  const bookingStatus: BookingStatus = useMemo(() => {
    const _bookingStatus: BookingStatus = {
      service: undefined,
      shop: undefined,
      timeSlot: undefined,
    };

    if (currentStep?.stepperOptions?.reassurance?.service) {
      _bookingStatus.service = countryService;
    }

    if (currentStep?.stepperOptions?.reassurance?.shop) {
      _bookingStatus.shop = shop;
    }

    if (currentStep?.stepperOptions?.reassurance?.timeSlot) {
      _bookingStatus.timeSlot = timeSlot;
    }

    return _bookingStatus;
  }, [currentStep, countryService, shop, timeSlot]);

  const skeletonHeight: string = useMemo(() => {
    let height = 0;

    const reassuranceConfig = currentStep?.stepperOptions?.reassurance;
    if (!reassuranceConfig) {
      return `${height}px`;
    }

    if (Object.values(reassuranceConfig)?.length > 0) {
      height = 28;
    }

    for (const value of Object.values(reassuranceConfig ?? {})) {
      if (value) {
        height += 33;
      }
    }

    return `${height}px`;
  }, [currentStep]);

  if (isInitializingData) {
    return (
      <div className='mb-4'>
        <Skeleton height={skeletonHeight} width='100%' />
      </div>
    );
  }

  return (
    <div className='mb-4'>
      <ReassuranceSection bookingStatus={bookingStatus} />
    </div>
  );
};

export default ReasssuranceWrapper;
